import React, { useCallback } from "react";
import clsx from "clsx";
import { round } from "lodash";
import differenceInDays from "date-fns/differenceInDays";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { AutoSizer } from "react-virtualized";
import { FixedSizeList } from "react-window";

import { Box, Grid, Tooltip, Zoom } from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import Drawer from "@material-ui/core/Drawer";
import Typography from "@material-ui/core/Typography";
import CloseIcon from "@material-ui/icons/Close";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import routes from "common/routes";
import { withClickBlocker } from "components/BlockableClickContext";
import { useLoggedInUser } from "components/LoggedInUserProvider";
import HorseShoeIcon from "components/Icons/HorseShoe";
import TableSortLabel from "components/TableSortLabel";
import Loader from "components/Loader";
import { useRacehorse360Api } from "hooks/api";
import PreferredConditionsMatchingHorsesSliderState, {
  IPreferredConditionsMatchingHorseInfo
} from "interfaces/PreferredConditionsMatchingHorsesSliderState";
import {
  setIsOpened,
  setOrder,
  setOrderBy,
  setSelectedPreferredConditions
} from "store/actions/preferredConditionsMatchingHorsesSlider";
import { parseCourseSurface } from "utils/enum-parser";
import { getSortFn } from "./helpers";
import useStyles from "./styles";

interface IPreferredConditionsValue {
  propName: string;
  render?: (data: string) => React.ReactNode;
}

const preferredConditions: IPreferredConditionsValue[] = [
  {
    propName: "surface",
    render: data => data && parseCourseSurface(Number(data))
  },
  {
    propName: "distance",
    render: data => data && data.replace(/([\d\s/])?( )([A-Z])/, "$1$3")
  },
  {
    propName: "raceType"
  },
  {
    propName: "claimingPrice",
    render: data =>
      data &&
      data.replace(
        /\d+,\d{3}/g,
        val => `${round(Number(val.replace(",", "")) / 1000)}K`
      )
  },
  {
    propName: "age",
    render: data => data && data.replace("yo", "")
  },
  {
    propName: "gender"
  },
  {
    propName: "stateBred",
    render: data => data && `${data} SB`
  }
];

enum EColumn {
  HORSE_NAME = "horseName",
  TRAINER_NAME = "trainerName",
  DAYS_OFF = "daysOff",
  IS_LAST_RACE = "isLastRace"
}

interface MatchingHorseCell {
  id: string;
  label: string;
  orderBy?: keyof IPreferredConditionsMatchingHorseInfo;
  hideDownSX720?: boolean;
  render?: (data: string) => React.ReactNode;
}

const tableColumns: MatchingHorseCell[] = [
  {
    id: EColumn.HORSE_NAME,
    label: "Horse",
    orderBy: EColumn.HORSE_NAME
  },
  {
    id: EColumn.TRAINER_NAME,
    label: "Trainer",
    orderBy: EColumn.TRAINER_NAME,
    hideDownSX720: true
  },
  {
    id: EColumn.DAYS_OFF,
    label: "Days Off",
    orderBy: EColumn.DAYS_OFF
  },
  {
    id: EColumn.IS_LAST_RACE,
    label: "Type",
    orderBy: EColumn.IS_LAST_RACE,
    render: data => (data ? "Last Race" : "Custom")
  }
];

const MatchingHorsesSlider = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { currentUser } = useLoggedInUser();

  const { isOpened, selectedPreferredConditions, order, orderBy } = useSelector(
    (state: {
      preferredConditionsMatchingHorsesSlider: PreferredConditionsMatchingHorsesSliderState;
    }) => state?.preferredConditionsMatchingHorsesSlider
  );

  const { useListHorses, useListHorseOnLists } = useRacehorse360Api();

  const {
    isFetching: isHorsesListFetching,
    isLoading: isHorseListLoading,
    data: horsesListData
  } = useListHorses(
    {
      query: {
        ids: selectedPreferredConditions?.horseIds
      },
      pagingOptions: {
        maxResults: 999
      },
      getOptions: {
        select: [
          "id",
          "name",
          "trainer.firstName",
          "trainer.middleName",
          "trainer.lastName",
          "lastRaceDate",
          "gender",
          "areLastRacePreferredConditionsSelected",
          "stateEligibleCode"
        ],
        orderBy: ["name"]
      }
    },
    {
      enabled: !!selectedPreferredConditions?.horseIds.length,
      initialData: {
        horses: []
      }
    }
  );

  const {
    isFetching: isHorseOnListsFetching,
    isLoading: isHorseOnListsLoading,
    data: horseOnListsData
  } = useListHorseOnLists(
    {
      query: {
        horseIds: selectedPreferredConditions?.horseIds,
        horseFacilityIds: currentUser.racingOfficialFacilityIds,
        activeOnly: { value: true }
      },
      pagingOptions: {
        maxResults: 999
      },
      getOptions: {
        select: ["horse.id", "listType"]
      }
    },
    {
      enabled: !!selectedPreferredConditions && !isHorseListLoading
    }
  );

  const horsesList = horsesListData?.horses;
  const horseOnLists = horseOnListsData?.horseOnLists;

  const preferredConditionsMatchingHorses: IPreferredConditionsMatchingHorseInfo[] =
    !isHorseListLoading && !isHorseOnListsLoading
      ? horsesList
          .map(h => {
            return {
              id: h.id,
              horseName: h.name,
              isStateBred:
                h.stateEligibleCode !==
                racehorse360.HorseStateEligibility.HORSE_STATE_ELIGIBILITY_NONE,
              trainerName: `${h.trainer.firstName} ${h.trainer.middleName} ${h.trainer.lastName}`,
              daysOff: differenceInDays(new Date(), new Date(h.lastRaceDate)),
              isLastRace: h.areLastRacePreferredConditionsSelected,
              lists: horseOnLists
                .filter(hol => hol.horse.id === h.id)
                .map(hol => hol.listType)
            };
          })
          .sort(getSortFn(orderBy, order))
      : [];

  const handleCloseClick = () => {
    dispatch(setIsOpened(false));
    dispatch(setSelectedPreferredConditions(undefined));
  };

  const handleSortClick = useCallback(
    (newOrderBy, newOrder) => {
      dispatch(setOrder(newOrder));
      dispatch(setOrderBy(newOrderBy));
    },
    [dispatch]
  );

  const renderPreferredConditionsSummary = () => {
    return (
      selectedPreferredConditions && (
        <Box className={classes.preferredConditionsSummary}>
          {preferredConditions.map((pc, i) => (
            <div key={i} className={classes.preferredConditionsSummaryCell}>
              {pc.render
                ? pc.render(selectedPreferredConditions[pc.propName])
                : selectedPreferredConditions[pc.propName]}
            </div>
          ))}
        </Box>
      )
    );
  };

  const renderMatchTypeSummary = () => (
    <Box className={classes.matchTypesSummary}>
      <div className={classes.matchTypeSummary}>
        <span
          className={clsx(
            classes.requestsCountCircle,
            classes.customRequestsCount
          )}
        >
          {preferredConditionsMatchingHorses.filter(x => !x.isLastRace).length}
        </span>
        Custom Requests
      </div>
      <div className={classes.matchTypeSummary}>
        <span
          className={clsx(
            classes.requestsCountCircle,
            classes.lastRaceMatchesCount
          )}
        >
          {preferredConditionsMatchingHorses.filter(x => x.isLastRace).length}
        </span>
        Last Race Matches
      </div>
    </Box>
  );

  const renderHorseOnListIcon = (
    item: IPreferredConditionsMatchingHorseInfo
  ) => {
    return (
      Boolean(item.lists?.length) && (
        <Tooltip
          title={
            <React.Fragment>
              {item.lists.map((listName, index) => (
                <Typography
                  key={index}
                  display="block"
                  className={classes.horseOnListTooltipTitle}
                >
                  {listName}
                </Typography>
              ))}
            </React.Fragment>
          }
          classes={{ tooltip: classes.horseOnListTooltip }}
          placement="bottom-start"
          TransitionComponent={Zoom}
        >
          <Grid
            item
            className={clsx(classes.badge, classes.horsesOnListsBadge)}
          >
            <HorseShoeIcon className={classes.badgeIcon} />
          </Grid>
        </Tooltip>
      )
    );
  };

  const renderRow = (index: number, style: React.CSSProperties) => {
    const currentItem = preferredConditionsMatchingHorses[index];
    return (
      <Link
        key={currentItem.id}
        style={style}
        className={classes.rowLink}
        to={{
          pathname: routes.horseDetails.path.replace(
            ":horseId",
            currentItem.id
          ),
          state: { fromPreferredConditions: true }
        }}
      >
        <div className={clsx(classes.tableRow)}>
          {tableColumns.map(cell => (
            <div
              key={`${currentItem.id}${cell.id}`}
              className={clsx(classes.tableCell, {
                [classes.hideDownSX720]: cell.hideDownSX720,
                [classes.cellRight]: cell.id === EColumn.IS_LAST_RACE,
                [classes[`lastRace-${currentItem[EColumn.IS_LAST_RACE]}`]]:
                  cell.id === EColumn.IS_LAST_RACE
              })}
            >
              {cell.id === EColumn.HORSE_NAME &&
                renderHorseOnListIcon(currentItem)}
              <div
                className={clsx({
                  [classes.ellipsis]: cell.id !== EColumn.IS_LAST_RACE
                })}
              >
                {cell.render
                  ? cell.render(currentItem[cell.id])
                  : currentItem[cell.id] || "-"}
                {cell.id === EColumn.HORSE_NAME &&
                  currentItem.isStateBred &&
                  " (SB)"}
              </div>
            </div>
          ))}
        </div>
      </Link>
    );
  };

  return (
    <Drawer
      open={isOpened}
      onClose={handleCloseClick}
      anchor="right"
      id="matchingHorsesSlider"
      role="menu"
      aria-labelledby="matchingHorsesSliderButton"
      className={classes.drawer}
    >
      <Box className={classes.root}>
        <Box className={classes.header}>
          <Typography>
            {preferredConditionsMatchingHorses.length} Total Requests
          </Typography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={handleCloseClick}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className={classes.content}>
          {renderPreferredConditionsSummary()}
          {renderMatchTypeSummary()}

          <div className={classes.tableContainer}>
            <div className={classes.tableHead}>
              {tableColumns.map(headerCell => (
                <div
                  className={clsx(classes.tableHeadCell, {
                    [classes.hideDownSX720]: headerCell.hideDownSX720,
                    [classes.cellRight]: headerCell.id === EColumn.IS_LAST_RACE
                  })}
                  key={headerCell.id}
                >
                  <TableSortLabel
                    key={headerCell.id}
                    name={headerCell.id}
                    order={order}
                    orderBy={orderBy}
                    onSortClick={handleSortClick}
                  >
                    {headerCell.label}
                  </TableSortLabel>
                </div>
              ))}
            </div>
            <div className={classes.tableBody}>
              {(isHorsesListFetching || isHorseOnListsFetching) && (
                <Loader overlay />
              )}
              <AutoSizer>
                {({ height, width }) => (
                  <FixedSizeList
                    itemCount={preferredConditionsMatchingHorses.length}
                    itemSize={40}
                    height={height}
                    width={width}
                  >
                    {({ index, style }) => renderRow(index, style)}
                  </FixedSizeList>
                )}
              </AutoSizer>
            </div>
          </div>
        </Box>
      </Box>
    </Drawer>
  );
};

export default withClickBlocker(MatchingHorsesSlider);
